import { useEffect, useState } from 'react';
import { DashboardService } from '../../../services/dashboard.service';
import { RepairBillService } from '../../../services/maintenance.service';
import { ActionItem } from '../../../interfaces/dashboard.interface';
import { ThreadLensKey } from '../../../interfaces/threadLens.interface';
import { useAuth } from '../../../context/AuthContext';
import { PERMISSIONS } from '../../../constants/permissions';
import { getWidget, isWidgetVisible } from '../registry';
import { isGatedModule } from '../registry/moduleMeta';
import { PresetKey } from '../registry/presets';
import { useDashboardAccess } from '../useDashboardAccess';
import { buildWorkActions } from '../board/workSummary';
import { loadPayrollActionItems } from '../board/payrollActionItems';

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Runs `call` only when allowed; a skipped or failed call resolves to null (so a 403 never breaks the board). */
const guarded = <T,>(allowed: boolean, call: () => Promise<T>): Promise<T | null> =>
    (allowed ? Promise.resolve().then(call).then(v => v ?? null).catch(() => null) : Promise.resolve(null));

/** Lenses whose server answer is complete, so the board adds no role work items on top. */
const OWN_TASK_LENSES: ThreadLensKey[] = ['sales', 'store', 'commercial', 'salesRep', 'maintenance', 'technician', 'personal', 'people'];

/** Items the Thread list already shows from the lens answer itself, so the work summary must not repeat them. */
const COVERED_BY_LENS = ['bill.overdue', 'bill.hold', 'invoice.overdue', 'ar.over60'];

/**
 * The same role tasks the Tiles Action Center shows (work summary, payroll approval, repair bills, and
 * pending expense claims for the manager), under the same permission gates. Leave is not fetched here:
 * the manager lens already carries it. Work items come first (critical before the rest), then payroll
 * and repair bills, as on Tiles.
 */
export function useLensWorkItems(institutionId: string, lens: ThreadLensKey): ActionItem[] {
    const [items, setItems] = useState<ActionItem[]>([]);
    const { hasPermission } = useAuth();
    const { enabledModules } = useDashboardAccess();

    useEffect(() => {
        // These lenses carry their own tasks and figures (the same ones their Tiles home shows): no extra work items.
        if (!institutionId || OWN_TASK_LENSES.includes(lens)) { setItems([]); return; }
        let live = true;
        const canSee = (widgetId: string) => {
            const def = getWidget(widgetId);
            return !!def && (!isGatedModule(def.module) || enabledModules.has(def.module)) && isWidgetVisible(def, hasPermission);
        };
        const preset: PresetKey | null = lens === 'owner' ? null : (lens as PresetKey);
        const canApprovePayroll = hasPermission(PERMISSIONS.PAYROLL_APPROVE);
        const canApproveRepairBills = enabledModules.has('accounting') && hasPermission(PERMISSIONS.SUPPLIER_BILL_CREATE);
        const manager = lens === 'manager';
        const canStore = enabledModules.has('inventory') && hasPermission(PERMISSIONS.DASHBOARD_INVENTORY_VIEW);

        Promise.allSettled([
            guarded(preset !== null, () => DashboardService.getWorkSummary(institutionId)),
            guarded(manager && canSee('expense.summary'), () => DashboardService.getExpenseSummary(institutionId)),
            guarded(manager && canSee('expense.queue'), () => DashboardService.getExpenseQueue(institutionId)),
            guarded(manager && canStore, () => DashboardService.getStoreAdminDashboard(institutionId)),
            guarded(canApprovePayroll, () => loadPayrollActionItems(institutionId)),
            guarded(canApproveRepairBills, () => RepairBillService.waitingCount(institutionId)),
        ]).then(([ws, expense, queue, store, payroll, repair]) => {
            if (!live) return;
            const work: ActionItem[] = ws.status === 'fulfilled' && ws.value && preset
                ? buildWorkActions(preset, ws.value).filter(i => !COVERED_BY_LENS.includes(i.id)) : [];
            if (expense.status === 'fulfilled' && expense.value && expense.value.pendingCount > 0) {
                const rows = queue.status === 'fulfilled' && Array.isArray(queue.value) ? queue.value : [];
                const oldest = Math.max(0, ...rows.map(r => r.waitingDays ?? 0));
                work.push({
                    id: 'expense.pending', severity: oldest > 14 ? 'CRITICAL' : 'WARNING', module: 'expense',
                    title: 'Expense claims awaiting approval',
                    detail: `${plural(expense.value.pendingCount, 'claim')} pending${oldest > 0 ? `, the oldest waiting ${plural(oldest, 'day')}` : ''}`,
                    count: expense.value.pendingCount, actionLabel: 'Review claims', link: '/expense/all-claims',
                });
            }
            // Same as Tiles for the manager: requisitions awaiting approval (low stock is dropped there too, it is a tile).
            const pendingReqs = store.status === 'fulfilled' && store.value ? store.value.pendingApprovalRequisitions : 0;
            if (pendingReqs > 0) {
                work.push({
                    id: 'requisition.pending', severity: 'WARNING', module: 'inventory',
                    title: 'Store requisitions awaiting approval',
                    detail: `${plural(pendingReqs, 'requisition')} waiting for a decision`,
                    count: pendingReqs, actionLabel: 'Review requisitions', link: '/pending-approval-requisitions',
                });
            }
            const payrollItems: ActionItem[] = payroll.status === 'fulfilled' && Array.isArray(payroll.value) ? payroll.value : [];
            const waiting = repair.status === 'fulfilled' && typeof repair.value === 'number' ? repair.value : 0;
            const repairItems: ActionItem[] = waiting > 0 ? [{
                id: 'repairbill.waiting', severity: 'WARNING', module: 'accounting', title: 'Repair bills waiting',
                detail: `${plural(waiting, 'repair bill')} from Maintenance waiting for approval`,
                count: waiting, actionLabel: 'Review bills', link: '/procurement/repair-bills',
            }] : [];
            setItems([...work.filter(i => i.severity === 'CRITICAL'), ...work.filter(i => i.severity !== 'CRITICAL'), ...payrollItems, ...repairItems]);
        });
        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId, lens, enabledModules]);

    return items;
}
